import { stylize } from "@envestnet/envreact-component-library";
import classnames from "classnames";
import CentricLayout from "components/CentricLayout";
import DashboardMfaLayout from "components/DashboardMfaLayout";
import EmailOtp from "components/EmailOtp";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { mfaFlow } from "utils/enum";
import withIntl from "utils/withIntl";

import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;

  username: string;

  // Email OTP in-place handlers
  onEmailOtpSend: (flow: mfaFlow) => void;
  onEmailOtpVerify: (payload: { code: string; flow: mfaFlow }) => void;
  onEmailOtpRemove: () => void;
  emailOtpLoading: boolean;
  emailOtpError: string | null;
  emailOtpVerifyDone: boolean;
  emailOtpRemoveDone: boolean;
  onEmailOtpReset: () => void;
  goToMfaScreen: (redirectUrl: string) => void;
}

interface DefaultProps {}

export interface EmailAuthenticatorLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & EmailAuthenticatorLayoutProps;

const EmailAuthenticatorLayout = (props: Props) => {
  const {
    classes,
    style,
    className,
    innerRef,
    onEmailOtpSend,
    onEmailOtpVerify,
    onEmailOtpRemove,
    emailOtpLoading,
    emailOtpError,
    emailOtpVerifyDone,
    emailOtpRemoveDone,
    onEmailOtpReset,
    goToMfaScreen,
    username,
  } = props;
  const { state } = useLocation();

  // After successful email OTP verify/remove, close the panel then reload the list
  useEffect(() => {
    if (emailOtpVerifyDone || emailOtpRemoveDone) {
      goToMfaScreen(state.navigationFrom);
      onEmailOtpReset();
    }
  }, [emailOtpVerifyDone, emailOtpRemoveDone]);

  return state?.currentLayout === mfaFlow.MFA ? (
    <DashboardMfaLayout>
      <CentricLayout className={classes.mfaCenter}>
        <EmailOtp
          mode={mfaFlow.MFA}
          onSend={onEmailOtpSend}
          onVerify={(code) => onEmailOtpVerify({ code, flow: mfaFlow.MFA })}
          onRemove={onEmailOtpRemove}
          onCancel={() => {
            onEmailOtpReset();
            goToMfaScreen(state.navigationFrom);
          }}
          loading={emailOtpLoading}
          error={emailOtpError}
        />
      </CentricLayout>
    </DashboardMfaLayout>
  ) : (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <CentricLayout className={classes.mfaCenter}>
        <EmailOtp
          mode={mfaFlow.SECURITY}
          onSend={onEmailOtpSend}
          onVerify={(code) =>
            onEmailOtpVerify({ code, flow: mfaFlow.SECURITY })
          }
          onRemove={onEmailOtpRemove}
          onCancel={() => {
            onEmailOtpReset();
            goToMfaScreen(state.navigationFrom);
          }}
          loading={emailOtpLoading}
          error={emailOtpError}
          username={username}
        />
      </CentricLayout>
    </div>
  );
};

EmailAuthenticatorLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(EmailAuthenticatorLayout)
);
